// AdminLayout — legacy wrapper kept as a thin shim around detail pages.
//
// Pre-app-shell, this component rendered its own sticky header +
// max-w-6xl gutter for every admin page. Now that AdminAppShellV2
// provides the top bar + bottom nav and each SPA's body owns the
// arena-tinted background, this wrapper just forwards children plus
// an optional in-page intro block (title / subtitle / right slot)
// for pages that want one.
//
// Phase 1 UI overhaul (2026-05-15): visual tokens unified with the
// player + agent SPAs.
//   • Title icon ring: violet accent (matches TopPillNav + TabHub
//     header) instead of standalone violet — same hue, but now
//     consistent across every admin surface.
//   • Title / subtitle text: arena-900 / arena-600 in light, white /
//     neon-200 in dark — same tokens MagentaTabBar + agent hubs use.
//
// IMPORTANT — no double-padding with TabHub.
// AdminLayout is used by DETAIL pages (AdminAgentDetail,
// AdminBotDetail, AdminDepositLedger, AdminOfferDetail,
// AdminRechargePlayer). It is NEVER composed inside a TabHub —
// every consumer is a top-level page reached through a sibling
// route, not a hub tab. The outer max-w-screen-2xl + px gutter +
// pt match TabHub's so detail pages and hub pages share identical
// horizontal/vertical rhythm. AdminAppShellV2's <main> has no
// horizontal padding of its own, so wrapping in AdminLayout is the
// page's only horizontal gutter — no compounding.

import { ReactNode } from 'react';
import Icon from '../../components/Icon';
import type { AdminThemePalette } from '../../games/types';

// Kept for type compatibility with per-game admin pages that import
// it. Values no longer drive the page header (the shell renders the
// chrome now) but per-game pages still use the palette internally for
// their own widget styling.
export const NEUTRAL_ADMIN_THEME: AdminThemePalette = {
  headerFrom:  'from-slate-900',
  headerTo:    'to-slate-800',
  ring:        'ring-slate-400',
  accent:      'bg-slate-700',
  accentHover: 'hover:bg-slate-600',
  dot:         'bg-slate-400',
  softBg:      'bg-slate-900/40',
  rangeAccent: 'accent-slate-500',
  badgeClasses: 'bg-arena-100/60 text-arena-700 border-arena-200/60',
};

type Props = {
  title?: string;
  subtitle?: string;
  titleIcon?: string;
  // theme is accepted but unused at the layout layer now — pages can
  // still read their own palette via per-game GameModule.adminTheme.
  theme?: AdminThemePalette;
  right?: ReactNode;
  children: ReactNode;
};

export default function AdminLayout({ title, subtitle, titleIcon, right, children }: Props) {
  // Render the optional in-page intro block ABOVE the children. The
  // shell's TopBar already shows the page name; the intro is for
  // sub-pages that want a richer summary (e.g. "Recharge a player —
  // pick from 12 active accounts").
  const showIntro = title || subtitle || right;
  return (
    // Default container — gives every legacy page proper top spacing
    // below the sticky TopPillNav and a max-width so wide screens
    // don't render edge-to-edge tables. Matches TabHub's outer
    // geometry (max-w-screen-2xl, px-2/3/6, py-3/4) so a hub page
    // and a detail page sit on the same horizontal/vertical grid.
    <div className="max-w-screen-2xl mx-auto px-2 sm:px-3 lg:px-6 pt-3 lg:pt-4 pb-4">
      {showIntro && (
        <header className="mb-4 flex items-start gap-3 px-2 lg:px-4">
          {titleIcon && (
            <span className="grid place-items-center w-10 h-10 rounded-xl
                             bg-violet-50 text-violet-700 ring-1 ring-violet-100
                             dark:bg-violet-400/15 dark:text-violet-300 dark:ring-violet-400/30 shrink-0">
              <Icon name={titleIcon} size={22} filled />
            </span>
          )}
          <div className="min-w-0 flex-1">
            {title && <h2 className="text-lg font-bold leading-tight truncate text-arena-900 dark:text-white">{title}</h2>}
            {subtitle && <p className="text-xs text-arena-600 dark:text-neon-200/70 truncate">{subtitle}</p>}
          </div>
          {right && <div className="shrink-0">{right}</div>}
        </header>
      )}
      {children}
    </div>
  );
}
